import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { captureSimulationError, captureApiError } from '@/lib/sentry';

interface EvaluationData {
  id: string;
  session_id: string;
  agent_id: string;
  simulation_id?: string;
  user_id: string;
  overall_score: number;
  detailed_evaluation: any;
  transcript?: string;
  evaluated_at: string;
  prize_eligible: boolean;
  is_submitted?: boolean;
  is_practice?: boolean;
  submitted_at?: string;
  deleted_at?: string;
  debrief_session_id?: string;
  coaching_session_id?: string;
  pathway_module_id?: string;
}

interface TeamPerformanceData {
  id: string;
  user_id: string;
  auth_user_id?: string;
  user_name: string;
  user_email: string;
  agent_id: string;
  company_id?: string;
  average_score: number;
  best_score: number;
  total_sessions: number;
  last_session_date: string;
}

export async function submitSimulationForEvaluation(data: {
  sessionId: string;
  agentId: string;
  agentName: string;
  agentType: string;
  userId: string;
  authUserId?: string;
  userName: string;
  userEmail: string;
  companyId?: string;
  platform: 'anam' | 'elevenlabs';
  conversationId?: string;
  conversationTranscript?: Array<{role: string; content: string; timestamp: string}> | string;
  evaluationCriteria: string;
  goalScore: number;
  sessionStart: string;
  sessionEnd: string;
}): Promise<{ success: boolean; message: string; evaluationId?: string }> {
  try {

    // Get auth user if not provided
    let authUserId = data.authUserId;
    if (!authUserId) {
      const { data: authData } = await supabase.auth.getUser();
      authUserId = authData.user?.id;
    }

    if (!authUserId) {
      throw new Error('Authentication required');
    }

    // Convert transcript to string if it's an array
    const transcriptString = Array.isArray(data.conversationTranscript)
      ? data.conversationTranscript.map(m => `${m.role}: ${m.content}`).join('\n')
      : data.conversationTranscript || '';

    const { data: result, error } = await supabase.functions.invoke('evaluate-simulation', {
      body: {
        session_id: data.sessionId,
        agent_id: data.agentId,
        agent_name: data.agentName,
        agent_type: data.agentType,
        user_id: data.userId,
        auth_user_id: authUserId,
        user_name: data.userName,
        user_email: data.userEmail,
        company_id: data.companyId,
        platform: data.platform,
        conversation_id: data.conversationId,
        conversation_transcript: transcriptString,
        evaluation_criteria: data.evaluationCriteria,
        goal_score: data.goalScore,
        session_start: data.sessionStart,
        session_end: data.sessionEnd,
        include_conversation_references: true
      }
    });

    if (error) {
      console.error('❌ Edge function error:', error);
      throw new Error(error.message || 'Evaluation failed');
    }

    if (!result?.success) {
      throw new Error(result?.error || 'Evaluation failed');
    }

    return {
      success: true,
      message: 'Evaluation completed successfully',
      evaluationId: result.evaluation_id
    };
  } catch (error) {
    console.error('❌ Failed to submit simulation:', error);
    captureSimulationError('evaluation', error, {
      agentId: data.agentId,
      agentName: data.agentName,
      userId: data.userId,
    });
    return {
      success: false,
      message: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

export async function fetchEvaluations(
  agentId: string, 
  userId: string
): Promise<EvaluationData[]> {
  try {
    // Get the authenticated user from Supabase session
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.error('❌ No authenticated user found');
      return [];
    }
    
    
    const { data, error } = await supabase
      .from('simulation_evaluations')
      .select('*')
      .eq('simulation_id', agentId)
      .eq('auth_user_id', user.id)
      .is('deleted_at', null)
      .order('evaluated_at', { ascending: false });
    
    if (error) {
      console.error('❌ Error fetching evaluations:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('❌ Failed to fetch evaluations:', error);
    captureApiError(error, { operation: 'fetch', table: 'simulation_evaluations', functionName: 'fetchSimulationEvaluations' });
    toast.error('Failed to load evaluations');
    return [];
  }
}

export async function fetchTeamPerformance(
  agentId: string,
  companyId?: string
): Promise<TeamPerformanceData[]> {
  try {
    
    let query = supabase
      .from('team_performance_cache')
      .select('*')
      .eq('agent_id', agentId)
      .order('average_score', { ascending: false })
      .limit(10);
    
    // Filter by company if provided
    if (companyId) {
      query = query.eq('company_id', companyId);
    }
    
    const { data, error } = await query;
    
    if (error) {
      console.error('❌ Error fetching team performance:', error);
      throw error;
    }

    return data || [];
  } catch (error) {
    console.error('❌ Failed to fetch team performance:', error);
    toast.error('Failed to load team performance');
    return [];
  }
}

interface PrizeClaim {
  id: string;
  user_id: string;
  agent_id: string;
  session_id: string;
  evaluation_id: string | null;
  prize_description: string | null;
  claimed_at: string;
  status: string;
}

export async function checkPrizeClaim(
  userId: string,
  agentId: string
): Promise<PrizeClaim | null> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      console.error('❌ No authenticated user found');
      return null;
    }
    
    
    const { data, error } = await supabase
      .from('prize_claims')
      .select('*')
      .eq('user_id', userId)
      .eq('agent_id', agentId)
      .maybeSingle();
    
    if (error) {
      console.error('❌ Error checking prize claim:', error);
      return null;
    }

    return data;
  } catch (error) {
    console.error('❌ Failed to check prize claim:', error);
    return null;
  }
}

export async function claimPrize(data: {
  userId: string;
  agentId: string;
  sessionId: string;
  evaluationId: string | null;
  goalScore: number;
}): Promise<{ success: boolean; message: string; claim?: PrizeClaim }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user) {
      return { success: false, message: 'Not authenticated' };
    }

    // Check if already claimed
    const existingClaim = await checkPrizeClaim(data.userId, data.agentId);
    if (existingClaim) {
      return { 
        success: false, 
        message: 'You have already claimed this reward',
        claim: existingClaim
      };
    }

    
    const prizeDescription = `Achievement Award for reaching goal score of ${data.goalScore}`;
    
    const { data: claim, error } = await supabase
      .from('prize_claims')
      .insert({
        user_id: data.userId,
        agent_id: data.agentId,
        session_id: data.sessionId,
        evaluation_id: data.evaluationId,
        prize_description: prizeDescription,
        status: 'claimed'
      })
      .select()
      .single();
    
    if (error) {
      console.error('❌ Error claiming prize:', error);
      throw error;
    }

    toast.success('🎉 Congratulations! Your reward has been claimed!');
    
    return { 
      success: true, 
      message: 'Prize claimed successfully',
      claim: claim 
    };
  } catch (error) {
    console.error('❌ Failed to claim prize:', error);
    toast.error('Failed to claim prize. Please try again.');
    return { 
      success: false, 
      message: error instanceof Error ? error.message : 'Unknown error'
    };
  }
}

// Submit a practice session for manager/team review.
export async function submitSession(evaluationId: string): Promise<{ success: boolean; message: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('Session expired. Please log in again.');
      return { success: false, message: 'Not authenticated' };
    }

    const { data, error } = await supabase
      .from('simulation_evaluations')
      .update({
        is_submitted: true,
        is_practice: false,
        submitted_at: new Date().toISOString()
      })
      .eq('id', evaluationId)
      .eq('auth_user_id', user.id)
      .select(); // Return updated row(s) to verify success

    if (error) throw error;
    
    // Check if any rows were actually updated
    if (!data || data.length === 0) {
      console.error('❌ No rows updated - evaluation not found or does not belong to user');
      console.error('   evaluationId:', evaluationId);
      console.error('   user.id:', user.id);
      toast.error('Could not submit session. The session may not exist or you may not have permission.');
      return { success: false, message: 'No rows updated' };
    }

    toast.success('Session submitted for manager review.');
    return { success: true, message: 'Session submitted successfully' };
  } catch (error) {
    console.error('❌ Submit error:', error);
    toast.error('Failed to submit session');
    return { success: false, message: 'Failed to submit session' };
  }
}

export interface ManagerReviewRecipient {
  profile_id: string;
  display_name: string | null;
  email: string;
  role: 'branch_admin' | 'unit_admin' | 'unit_manager';
  unit_id: string;
  unit_name: string;
  branch_id: string;
  branch_name: string;
}

export async function getManagerReviewRecipients(): Promise<ManagerReviewRecipient[]> {
  const enterpriseDb = supabase as any;
  const { data, error } = await enterpriseDb.rpc('get_my_report_recipients');

  // Shared MEDD and older white-label deployments do not have the enterprise
  // hierarchy RPC. Their existing direct-submit behaviour remains unchanged.
  if (error?.code === 'PGRST202' || error?.code === '42883') return [];
  if (error) throw error;
  return (data || []) as ManagerReviewRecipient[];
}

export async function submitSessionForManagerReview(
  evaluationId: string,
  recipientProfileId: string,
  note?: string,
): Promise<{ success: boolean; message: string }> {
  try {
    const enterpriseDb = supabase as any;
    const { error } = await enterpriseDb.rpc('submit_evaluation_for_review', {
      p_evaluation_id: evaluationId,
      p_recipient_profile_id: recipientProfileId,
      p_note: note?.trim() || null,
    });

    if (error) throw error;
    toast.success('Session submitted to your selected manager.');
    return { success: true, message: 'Session submitted successfully' };
  } catch (error) {
    console.error('Failed to submit session to manager:', error);
    const message = error instanceof Error ? error.message : 'Failed to submit session';
    toast.error(message);
    return { success: false, message };
  }
}

// Soft-delete a practice session
export async function deleteSession(evaluationId: string): Promise<{ success: boolean; message: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast.error('Session expired. Please log in again.');
      return { success: false, message: 'Not authenticated' };
    }

    const { data, error } = await supabase
      .from('simulation_evaluations')
      .update({ deleted_at: new Date().toISOString() })
      .eq('id', evaluationId)
      .eq('auth_user_id', user.id)
      .eq('is_submitted', false)
      .select(); // Return updated row(s) to verify success

    if (error) throw error;
    
    // Check if any rows were actually updated
    if (!data || data.length === 0) {
      console.error('❌ No rows deleted - evaluation not found, not a practice session, or does not belong to user');
      console.error('   evaluationId:', evaluationId);
      console.error('   user.id:', user.id);
      toast.error('Could not delete session. The session may not exist or is already submitted.');
      return { success: false, message: 'No rows updated' };
    }

    toast.success('Practice session deleted');
    return { success: true, message: 'Session deleted' };
  } catch (error) {
    console.error('❌ Delete error:', error);
    toast.error('Failed to delete session');
    return { success: false, message: 'Failed to delete session' };
  }
}

// Fetch sessions by status (practice or submitted)
export async function fetchSessionsByStatus(
  status: 'practice' | 'submitted'
): Promise<any[]> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return [];

    let query = supabase
      .from('simulation_evaluations')
      .select(`
        *,
        simulation_agents!simulation_evaluations_simulation_id_fkey(
          name,
          type,
          image_url
        )
      `)
      .eq('auth_user_id', user.id)
      .is('deleted_at', null);

    if (status === 'practice') {
      // Practice = not yet submitted (includes all unsubmitted sessions)
      query = query.eq('is_submitted', false)
        .order('evaluated_at', { ascending: false });
    } else {
      // Submitted = explicitly submitted by the user
      query = query.eq('is_submitted', true)
        .order('submitted_at', { ascending: false });
    }

    const { data, error } = await query;

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('❌ Failed to fetch sessions:', error);
    return [];
  }
}
